import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStateContext } from '../../contexts/ContextProvider';
import { usePermissions } from '../../contexts/PermissionContext';
import { defaultRouteOrder, routePermissions } from '../../config/routePermissions';

/**
 * Composant invisible qui écoute les événements 'permission-denied'
 * et redirige l'utilisateur vers la première page accessible.
 */
export default function PermissionErrorHandler() {
    const navigate = useNavigate();
    const { isUserLoading, user } = useStateContext();
    const { hasPermission, hasAnyPermission, isSuperAdmin } = usePermissions();

    useEffect(() => {
        const handlePermissionDenied = () => {
            // Ne pas gérer si l'utilisateur n'est pas encore chargé
            if (isUserLoading || user.id === 0) return;

            // Trouver la première route accessible et rediriger silencieusement
            const firstAccessible = getFirstAccessibleRoute();
            navigate(firstAccessible, { replace: true });
        };

        window.addEventListener('permission-denied', handlePermissionDenied);
        return () => window.removeEventListener('permission-denied', handlePermissionDenied);
    }, [navigate, isUserLoading, user.id, hasPermission, hasAnyPermission, isSuperAdmin]);

    /**
     * Trouve la première route accessible pour l'utilisateur
     */
    function getFirstAccessibleRoute(): string {
        // Super admin redirigé vers le dashboard
        if (isSuperAdmin) return '/dashboard';

        for (const route of defaultRouteOrder) {
            const perm = routePermissions[route];

            // Pas de permission requise pour cette route
            if (perm === undefined) return route;

            // Route super-admin only, skip
            if (perm === null) continue;

            // Vérifier si l'utilisateur a la permission
            const hasAccess = Array.isArray(perm)
                ? hasAnyPermission(perm)
                : hasPermission(perm);

            if (hasAccess) return route;
        }

        // Fallback vers settings
        return '/settings';
    }

    // Composant invisible - ne rend rien
    return null;
}
